import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { SectionHeading } from '../components/ui/SectionHeading';
import { PublicRoute } from '../types/publicTypes';
import {
  CheckCircle2,
  XCircle,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Building2,
  Anchor,
  Smartphone,
  Cpu,
  Layers,
  Search,
  MessageSquare,
  HelpCircle,
} from 'lucide-react';

interface SolutionScope {
  id: string;
  name: string;
  category: string;
  badge?: string;
  popular?: boolean;
  tagline: string;
  description: string;
  targetAudience: string;
  ctaText: string;
  ctaAction: PublicRoute;
  deliverables: string[];
  features: { text: string; included: boolean; highlight?: boolean }[];
}

const SOLUTIONS_DATA: SolutionScope[] = [
  {
    id: 'corporate',
    name: 'Kurumsal Web Sitesi Çözümü',
    category: 'Web Platformu',
    badge: 'En Çok Tercih Edilen',
    popular: true,
    tagline: 'Holdingler, danışmanlık firmaları ve profesyonel hizmet sunan işletmeler için.',
    description: 'AI destekli görsel sürükle-bırak editör, kurumsal şablonlar, özel domain bağlama ve arama motorlarında üst sıralara çıkaran SEO altyapısı.',
    targetAudience: 'Şirketler, klinikler, hukuk büroları, yerel ve ulusal markalar',
    ctaText: 'Site Oluştur',
    ctaAction: '/kayit',
    deliverables: ['AI + Görsel Editör', 'SEO & Sitemap', 'Özel Domain', 'SSL', 'Blog/Haberler'],
    features: [
      { text: 'Görsel Sürükle & Bırak Web Sitesi Editörü', included: true, highlight: true },
      { text: 'Ödüllü Kurumsal Sektörel Tema Kütüphanesi', included: true },
      { text: 'Özel Alan Adı Bağlama (.com, .com.tr)', included: true },
      { text: 'Otomatik 256-Bit SSL ve Global CDN', included: true },
      { text: 'Kurumsal Blog, Proje Vitrini ve Hizmet Yönetimi', included: true },
      { text: 'Gelişmiş SEO Analizi ve OpenGraph Önizleme', included: true, highlight: true },
      { text: 'İletişim & Teklif Toplama Formları', included: true },
      { text: '3 Gün Boyunca Risksiz Ücretsiz Deneme', included: true },
    ],
  },
  {
    id: 'yacht',
    name: 'Lüks Yat Kiralama & Rezervasyon',
    category: 'Özel Sektörel Çözüm',
    badge: 'Yatçılık Özel Paketi',
    popular: false,
    tagline: 'Yat kiralama acenteleri, kaptanlar ve mavi tur operatörleri için anahtar teslim.',
    description: 'Yat filosu yönetimi, interaktif müsaitlik ve doluluk takvimi, müşteri rezervasyon motoru ve WhatsApp anında satış entegrasyonu.',
    targetAudience: 'Yat kiralama acenteleri, gulet ve motoryat sahipleri, marinalar',
    ctaText: 'Yat Sitesi Oluştur',
    ctaAction: '/kayit',
    deliverables: ['Filo Yönetimi', 'Müsaitlik Takvimi', 'Rezervasyon Motoru', 'WhatsApp Satış'],
    features: [
      { text: 'Dinamik Yat Filosu Yönetimi (Kabin, Boy, Liman, Motor)', included: true, highlight: true },
      { text: 'Müşteri Rezervasyon & Fiyat Teklifi Talep Motoru', included: true, highlight: true },
      { text: 'İnteraktif Müsaitlik ve Doluluk Takvimi', included: true, highlight: true },
      { text: 'Ultra-Lüks Yatçılık ve Marina Tasarımları', included: true },
      { text: 'Çoklu Para Birimi (EUR, USD, TRY) Desteği', included: true },
      { text: 'WhatsApp Hızlı Teklif ve Bildirim Entegrasyonu', included: true },
      { text: 'Özel Alan Adı, SSL ve Yüksek Hızlı Hosting', included: true },
      { text: '3 Gün Boyunca Risksiz Ücretsiz Deneme', included: true },
    ],
  },
  {
    id: 'mobile_app',
    name: 'Özel Mobil Uygulama Geliştirme',
    category: 'Mobil Ürün',
    badge: 'Mobil Yazılım',
    popular: false,
    tagline: 'Saha operasyonları, denetim ekipleri ve müşteriye özel mobil uygulamalar.',
    description: 'ESFA Yangın gibi saha denetim ekipleri için QR kod tarama, periyodik bakım takibi, anlık raporlama ve iOS/Android mobil yazılımı.',
    targetAudience: 'Saha denetim firmaları, servis ağları, yangın güvenliği, lojistik',
    ctaText: 'Teklif Alın',
    ctaAction: '/iletisim',
    deliverables: ['iOS & Android Native', 'QR Kod Okuyucu', 'Çevrim Dışı Mod', 'Cloud Sync'],
    features: [
      { text: 'iOS ve Android Platformlarına Özel Native Kodlama', included: true, highlight: true },
      { text: 'Saha Ekipleri İçin QR Kod ve Barkod Tarama', included: true, highlight: true },
      { text: 'İnternetsiz Ortamlarda Çevrim Dışı (Offline) Çalışma', included: true },
      { text: 'Merkezi Yönetim Paneli ve Anlık Bulut Senkronizasyonu', included: true },
      { text: 'Müşteriye Özel PDF Rapor ve Sertifika Üretimi', included: true },
      { text: 'Push Bildirimleri ve Görev Atama Sistemi', included: true },
      { text: 'Uçtan Uca Kurulum ve Teknik Bakım Desteği', included: true },
    ],
  },
  {
    id: 'enterprise_services',
    name: 'Özel Yazılım & Dijital Hizmetler',
    category: 'Kurumsal & Büyüme',
    badge: 'Büyüme Odaklı',
    tagline: 'Google İşletme Profili ve kurumsal sistem entegrasyonları.',
    description: 'Web sitenizin ötesinde; Google Haritalar yerel SEO yönetimi, özel API bağlantısı ve kurumsal ERP/CRM entegrasyonu.',
    targetAudience: 'Bölgesinde lider olmak isteyen markalar ve büyüme aşamasındaki firmalar',
    ctaText: 'Görüşme Talep Edin',
    ctaAction: '/iletisim',
    deliverables: ['Google İşletme SEO', 'Özel API Entegrasyonu', 'ERP/CRM Entegrasyonu', 'VIP Destek'],
    features: [
      { text: 'Google İşletme Profili Doğrulama & Harita SEO Çalışması', included: true, highlight: true },
      { text: 'Özel API ve Kurumsal CRM/ERP Entegrasyonu', included: true, highlight: true },
      { text: 'Çoklu Şube ve Bayi Yönetim Mimarisi (Multi-Tenant)', included: true },
      { text: 'Aday Müşteri İçe/Dışa Aktarma ve Özel Raporlama', included: true },
      { text: 'Size Özel Atanmış Teknik Proje Yöneticisi', included: true },
      { text: 'Öncelikli 7/24 Teknik Danışmanlık ve Destek', included: true },
    ],
  },
];

export const PricingPage: React.FC = () => {
  const { navigate } = useNavigation();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24 selection:bg-blue-600 selection:text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <SectionHeading
          badge="İşletme Çözümleri & Paket Kapsamı"
          title="İhtiyacınıza Uygun Kurumsal Çözümü Seçin"
          subtitle="Kurumsal web sitelerinden lüks yat kiralamaya, özel mobil saha uygulamalarından Google dijital servislerine kadar tüm yeteneklerimizi keşfedin."
        />

        {/* Quick Solution Selector Pills */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs font-semibold">
          <span className="text-slate-400">Hızlı Erişim:</span>
          <a
            href="#solution-corporate"
            className="px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-200 hover:text-white hover:border-blue-500/50 transition flex items-center gap-1.5"
          >
            <Building2 className="w-3.5 h-3.5 text-blue-400" />
            <span>Kurumsal Web Sitesi</span>
          </a>
          <a
            href="#solution-yacht"
            className="px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-200 hover:text-white hover:border-blue-500/50 transition flex items-center gap-1.5"
          >
            <Anchor className="w-3.5 h-3.5 text-blue-400" />
            <span>Yat Kiralama & Rezervasyon</span>
          </a>
          <a
            href="#solution-mobile_app"
            className="px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-200 hover:text-white hover:border-blue-500/50 transition flex items-center gap-1.5"
          >
            <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
            <span>Mobil Uygulama</span>
          </a>
          <a
            href="#solution-enterprise_services"
            className="px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-200 hover:text-white hover:border-blue-500/50 transition flex items-center gap-1.5"
          >
            <Cpu className="w-3.5 h-3.5 text-indigo-400" />
            <span>Özel Yazılım & Google Servisleri</span>
          </a>
        </div>

        {/* Solution Cards Grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SOLUTIONS_DATA.map((solution) => (
            <div
              key={solution.id}
              id={`solution-${solution.id}`}
              className={`rounded-3xl p-6 sm:p-7 flex flex-col justify-between transition-all duration-300 relative ${
                solution.popular
                  ? 'bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-blue-500 shadow-2xl shadow-blue-600/20'
                  : 'bg-slate-900/70 border border-slate-800 hover:border-slate-700 shadow-xl'
              }`}
            >
              {/* Popular / Custom Badge */}
              {solution.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold bg-blue-600 text-white shadow-md shadow-blue-600/40 uppercase tracking-wider whitespace-nowrap">
                  {solution.badge}
                </div>
              )}

              <div>
                <div className="mb-4">
                  <span className="text-[11px] font-bold text-blue-400 uppercase tracking-wider block mb-1">
                    {solution.category}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-2 leading-tight">
                    {solution.name}
                  </h3>
                  <p className="text-xs text-slate-400 min-h-[42px] leading-relaxed">
                    {solution.tagline}
                  </p>
                </div>

                {/* Scope Highlights Chips */}
                <div className="py-3 border-y border-slate-800/80 mb-6">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                    Kapsam Çözümleri:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {solution.deliverables.map((item, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 text-[11px] border border-slate-700 font-medium"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Dahil Olan Özellikler
                  </span>
                  {solution.features.map((feat, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 text-xs text-slate-200"
                    >
                      <CheckCircle2
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          feat.highlight ? 'text-blue-400' : 'text-emerald-400'
                        }`}
                      />
                      <span className={feat.highlight ? 'font-semibold text-white' : ''}>
                        {feat.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card CTA button */}
              <button
                onClick={() => navigate(solution.ctaAction)}
                className={`w-full py-3.5 px-4 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 shadow-md flex items-center justify-center gap-2 active:scale-95 ${
                  solution.popular
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-600/30'
                    : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                }`}
              >
                <span>{solution.ctaText}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* 3-Day Risk-Free Trial Callout Banner */}
        <div className="mt-16 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-blue-950/40 via-slate-900 to-slate-900 border border-blue-500/20 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-600/15 border border-blue-500/30 text-blue-400 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-white font-bold text-base">
                3 Günlük Ücretsiz Deneme — Kredi Kartı Gerekmez
              </h4>
              <p className="text-slate-300 text-xs sm:text-sm mt-0.5">
                Kayıt olduğunuz anda sisteminizin tüm yeteneklerini hemen test edebilir, ilk web sitenizi oluşturmaya başlayabilirsiniz.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/kayit')}
            className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-bold whitespace-nowrap shadow-lg shadow-blue-600/30 transition flex items-center gap-2"
          >
            <span>Hemen Başlayın</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Feature & Module Capability Matrix */}
        <div className="mt-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Modül Matrisi</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Çözüm ve Modül Kapsamları
            </h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Tüm sektörlerin operasyonel ihtiyaçlarına uygun hazır modüller.
            </p>
          </div>

          <div className="overflow-x-auto border border-slate-800 rounded-2xl bg-slate-900/60 shadow-xl">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-900 text-slate-400 font-semibold uppercase tracking-wider border-b border-slate-800">
                <tr>
                  <th className="p-4 sm:p-5">Modül & Yetenek</th>
                  <th className="p-4 text-center text-white">Kurumsal Web</th>
                  <th className="p-4 text-center text-blue-400">Lüks Yat Kiralama</th>
                  <th className="p-4 text-center text-emerald-400">Özel Mobil Uygulama</th>
                  <th className="p-4 text-center text-indigo-400">Özel Yazılım & Google Servisleri</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                <tr>
                  <td className="p-4 font-semibold text-white">Temel Amaç</td>
                  <td className="p-4 text-center">Dijital Kurumsal Vitrin</td>
                  <td className="p-4 text-center text-blue-400">Filo & Rezervasyon Motoru</td>
                  <td className="p-4 text-center text-emerald-400">Saha Takip & QR Kod</td>
                  <td className="p-4 text-center text-indigo-400">Entegrasyon & Altyapı</td>
                </tr>
                <tr>
                  <td className="p-4">Görsel Sürükle-Bırak Editör</td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4">Özel Alan Adı (.com/.com.tr) & SSL</td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4">Yat Filosu & Müsaitlik Takvimi</td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4">Kurumsal Blog & Portfolyo Vitrini</td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4">QR Kod & Saha Denetim Modülü</td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4">Harita SEO & Altyapı Yönetimi</td>
                  <td className="p-4 text-center">Opsiyonel</td>
                  <td className="p-4 text-center">Opsiyonel</td>
                  <td className="p-4 text-center"><XCircle className="w-4 h-4 text-slate-700 mx-auto" /></td>
                  <td className="p-4 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4">Teknik Destek Seviyesi</td>
                  <td className="p-4 text-center font-medium text-white">Canlı Destek</td>
                  <td className="p-4 text-center text-blue-400 font-medium">Öncelikli Yatçılık Desteği</td>
                  <td className="p-4 text-center text-emerald-400 font-medium">Özel Mobil Geliştirici</td>
                  <td className="p-4 text-center text-indigo-400 font-bold">Özel Proje Yöneticisi</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
